import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

export default function PricingPage() {
  const plans = [
    {
      name: "Free",
      price: "0",
      period: "forever",
      description: "Perfect for getting started",
      features: [
        "1 Organization",
        "25 Invoices per month",
        "50 Customers",
        "100 Inventory items",
        "Basic invoice templates",
        "Email support",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "PRO",
      price: "0",
      strikedPrice: "999",
      period: "annually",
      description: "For growing businesses with higher volume",
      features: [
        "Unlimited Organizations",
        "Unlimited Invoices",
        "Unlimited Customers",
        "Unlimited Inventory items",
        "Custom invoice templates",
        "Priority email support",
        "Inventory history tracking",
        "Export to PDF",
      ],
      cta: "Get Started",
      popular: true,
    },
  ];

  const faqs = [
    {
      question: "Can I change my plan later?",
      answer:
        "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately and we'll prorate your billing.",
    },
    {
      question: "What happens if I exceed my limits?",
      answer:
        "We'll notify you when you're approaching your limits. You can upgrade your plan to continue using the service without interruption.",
    },
    {
      question: "Is there a free trial?",
      answer:
        "Both plans are currently free. No credit card required to start. You can upgrade, downgrade, or cancel anytime.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards, debit cards, and UPI payments for Indian customers. Enterprise plans can also pay via bank transfer.",
    },
    {
      question: "Can I cancel my subscription?",
      answer:
        "Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your current billing period.",
    },
  ];

  return (
    <>
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for Bill Forge. Choose from Free or PRO plans - both completely free for now. No credit card required. Professional invoicing software for small businesses."
        path="/pricing"
      />
      <div className="bg-background">
      {/* Header */}
      <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your business. Both plans are currently free.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-card border rounded-2xl p-8 ${
                  plan.popular
                    ? "border-primary shadow-lg scale-105"
                    : "border-border hover:border-primary/50"
                } transition-all`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-full px-4">
                    <span className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-xs sm:text-sm font-bold px-3 py-1 rounded-full shadow-lg whitespace-nowrap inline-block">
                      🔥 100% OFF - STEAL DEAL
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold font-manrope mb-2">{plan.name}</h3>
                  <p className="text-muted-foreground mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    {plan.strikedPrice && (
                      <span className="text-2xl text-muted-foreground line-through mr-2">
                        ₹{plan.strikedPrice}
                      </span>
                    )}
                    <span className="text-4xl font-bold">₹{plan.price}</span>
                    <span className="text-muted-foreground ml-2">/{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <span className="text-primary mr-3 mt-1">✓</span>
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`block w-full py-3 text-center font-medium rounded-lg transition-colors ${
                    plan.popular
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-manrope text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-background border border-border rounded-lg p-6"
              >
                <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl opacity-90 mb-10">
            Get started for free today. No credit card required.
          </p>
          <Link
            to="/signup"
            className="inline-block px-8 py-4 text-lg font-medium bg-background text-foreground rounded-lg hover:bg-background/90 transition-colors"
          >
            Get Started
          </Link>
        </div>
      </section>
    </div>
    </>
  );
}
